"use client";

export type AppTheme = "dark" | "light" | "psone" | "system";

export const APP_THEME_STORAGE_KEY = "app-theme-preference";
export const APP_THEME_ATTR = "data-app-theme";
const APP_THEME_EVENT = "imags:app-theme-change";

function normalizeTheme(value: string | null | undefined): AppTheme {
  return value === "dark" || value === "psone" || value === "system"
    ? value
    : "light";
}

export function getStoredAppTheme(): AppTheme {
  if (typeof window === "undefined") return "light";
  return normalizeTheme(window.localStorage.getItem(APP_THEME_STORAGE_KEY));
}

export function getAppliedAppTheme(): AppTheme {
  if (typeof document === "undefined") return "light";
  return normalizeTheme(
    document.documentElement.getAttribute(APP_THEME_ATTR) ??
      (typeof window !== "undefined"
        ? window.localStorage.getItem(APP_THEME_STORAGE_KEY)
        : null),
  );
}

export function applyAppTheme(theme: AppTheme) {
  if (typeof document === "undefined") return;

  const normalizedTheme = normalizeTheme(theme);
  const root = document.documentElement;
  root.setAttribute(APP_THEME_ATTR, normalizedTheme);

  if (typeof window !== "undefined") {
    window.localStorage.setItem(APP_THEME_STORAGE_KEY, normalizedTheme);
    window.dispatchEvent(
      new CustomEvent<AppTheme>(APP_THEME_EVENT, { detail: normalizedTheme }),
    );
  }
}

export function subscribeToAppTheme(onStoreChange: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === APP_THEME_STORAGE_KEY) {
      onStoreChange();
    }
  };

  const handleThemeChange = () => {
    onStoreChange();
  };

  window.addEventListener("storage", handleStorage);
  window.addEventListener(APP_THEME_EVENT, handleThemeChange);

  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(APP_THEME_EVENT, handleThemeChange);
  };
}
